import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { PlatformEvent, SimulationSeed, ActiveBranch } from '../../types/index.ts';
import { normalizeBranches } from '../../types/index.ts';
import type { RootState } from '../index.ts';
import { loadScenario } from '../actions/loadScenario.ts';

export type EventSource = 'seed' | 'subscription';

export interface EventsState {
  scenarioId: string | null;
  scenarioLabel: string | null;
  description: string | null;
  given: string | null;
  when: string | null;
  then: string | null;
  expectedPath: string[];
  activeBranches: ActiveBranch[];
  events: PlatformEvent[];
  source: EventSource | null;
  totalExpected: number | null;
  sealed: boolean;
}

const initialState: EventsState = {
  scenarioId: null,
  scenarioLabel: null,
  description: null,
  given: null,
  when: null,
  then: null,
  expectedPath: [],
  activeBranches: [],
  events: [],
  source: null,
  totalExpected: null,
  sealed: false,
};

const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    loadSeed(state, action: PayloadAction<SimulationSeed>) {
      // Write-once: if already sealed, ignore subsequent loads
      if (state.sealed) return;

      const seed = action.payload;
      state.scenarioId = seed.scenarioId;
      state.scenarioLabel = seed.scenarioLabel;
      state.description = seed.description;
      state.given = seed.given ?? null;
      state.when = seed.when ?? null;
      state.then = seed.then ?? null;
      state.expectedPath = seed.expectedPath;
      state.activeBranches = normalizeBranches(seed.activeBranches);
      state.events = seed.events;
      state.source = 'seed';
      state.totalExpected = seed.events.length;
      state.sealed = true;
    },

    appendEvent(state, action: PayloadAction<PlatformEvent>) {
      // Subscription mode: only works when source is "subscription"
      // or when no seed has been loaded yet
      if (state.sealed) return;

      if (state.source === null) {
        state.source = 'subscription';
      }
      if (state.source !== 'subscription') return;

      state.events.push(action.payload);
    },

    initSubscription(state, action: PayloadAction<{ scenarioId: string; scenarioLabel: string }>) {
      if (state.sealed) return;

      state.scenarioId = action.payload.scenarioId;
      state.scenarioLabel = action.payload.scenarioLabel;
      state.source = 'subscription';
    },

    resetEvents() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadScenario, (_, action) => {
      const seed = action.payload.seed;
      return {
        scenarioId: seed.scenarioId,
        scenarioLabel: seed.scenarioLabel,
        description: seed.description,
        given: seed.given ?? null,
        when: seed.when ?? null,
        then: seed.then ?? null,
        expectedPath: seed.expectedPath,
        activeBranches: normalizeBranches(seed.activeBranches),
        events: seed.events,
        source: 'seed' as EventSource,
        totalExpected: seed.events.length,
        sealed: true,
      };
    });
  },
});

export const { loadSeed, appendEvent, initSubscription, resetEvents } = eventsSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectScenarioId = (state: RootState) => state.events.scenarioId;
export const selectScenarioLabel = (state: RootState) => state.events.scenarioLabel;
export const selectDescription = (state: RootState) => state.events.description;
export const selectGiven = (state: RootState) => state.events.given;
export const selectWhen = (state: RootState) => state.events.when;
export const selectThen = (state: RootState) => state.events.then;
export const selectExpectedPath = (state: RootState) => state.events.expectedPath;
export const selectActiveBranches = (state: RootState) => state.events.activeBranches;
export const selectEvents = (state: RootState) => state.events.events;
export const selectEventSource = (state: RootState) => state.events.source;
export const selectTotalExpected = (state: RootState) => state.events.totalExpected;
export const selectTotalEvents = (state: RootState) => state.events.events.length;
export const selectEventsSealed = (state: RootState) => state.events.sealed;

export const selectEventAtIndex = (state: RootState, index: number): PlatformEvent | undefined => {
  if (index < 0 || index >= state.events.events.length) return undefined;
  return state.events.events[index];
};

export default eventsSlice.reducer;
